<template>
  <div class="mobile-language-selector">
    <div class="p-panelmenu-item depth-0" role="button">
      <div class="p-panelmenu-content" role="button">
        <a v-ripple class="p-panelmenu-item-link" :data-expanded="isOpen" @click="toggleOpen">
          <span class="pi pi-globe p-panelmenu-item-icon"></span>
          <span class="p-panelmenu-item-label">{{ t('navigation.language') }}</span>
          <span :class="['pi ml-auto chevron-icon', isOpen ? 'pi-angle-down' : 'pi-angle-right']" />
        </a>
      </div>
    </div>

    <Transition name="slide-down">
      <div v-if="isOpen" class="flags-container">
        <button
          v-for="localeOption in locales"
          :key="localeOption.locale"
          class="flag-button"
          :class="{ active: selectedLocale?.locale === localeOption.locale }"
          :title="localeOption.name"
          @click="selectLanguage(localeOption.locale)"
        >
          <img :src="localeOption.img" :alt="localeOption.name" class="flag-img" />
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useLanguageSelector } from '@/composables/useLanguageSelector';
import type { SupportedLocale } from '@/plugins/i18n';

const { t } = useI18n();
const { locales, selectedLocale, selectLanguage: setLanguage } = useLanguageSelector();
const isOpen = ref(false);

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

function selectLanguage(newLocale: SupportedLocale) {
  setLanguage(newLocale);
  isOpen.value = false;
}
</script>

<style scoped lang="css">
.mobile-language-selector {
  width: 100%;
}

.mobile-language-selector :deep(.chevron-icon) {
  color: #ffffff !important;
  font-size: 0.875rem;
}

.flags-container {
  display: flex;
  gap: 2.5rem;
  padding: 0.75rem 1rem 1rem;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.flag-button {
  background: transparent;
  border: 2px solid transparent;
  border-radius: 4px;
  padding: 0.125rem;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.flag-button:hover {
  border-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.flag-button.active {
  border-color: var(--primary-color);
  box-shadow: 0 0 8px rgba(var(--primary-color-rgb), 0.5);
}

.flag-img {
  width: 1.5rem;
  height: 1.5rem;
  display: block;
  border-radius: 2px;
}

/* Slide down animation */
.slide-down-enter-active {
  animation: slideDown 0.2s ease-out;
}

.slide-down-leave-active {
  animation: slideUp 0.2s ease-in;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}
</style>
